import React, { Fragment, useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
  ListSubheader,
} from "@mui/material";
import { AddRounded, HistoryRounded } from "@mui/icons-material";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { PID_URL } from "../../../../utils/routes";
import { useDispatch, useSelector } from "react-redux";
import {
  createSession,
  getAllSessions,
} from "../../../../core/repo/sessionRepo";
import Loader from "../../../../components/Loader";
import { groupByDate } from "../../../../utils/utils";

const useStyles = makeStyles()((theme) => ({
  iconButton: {
    color: theme.palette.text.primary,
  },
}));

function Header() {
  const history = useHistory();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { selectedDomain } = useSelector((state) => state.domain);
  const location = useLocation();
  const fileId = new URLSearchParams(location.search).get("fileId");
  const tabId = new URLSearchParams(location.search).get("tabId");

  const handleNewChat = async () => {
    try {
      const response = await dispatch(createSession(selectedDomain.id, "pid"));
      let queryParams = new URLSearchParams();
      queryParams.set("fileId", fileId);
      queryParams.set("tabId", tabId);
      history.push(`${PID_URL}/${response.id}?${queryParams.toString()}`);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Box
      px={2}
      py={1}
      display="flex"
      alignItems="center"
      justifyContent="space-between">
      <Typography fontWeight={500}>Ask Questions</Typography>
      <Box display={"flex"} alignItems={"center"} gap={1}>
        <Tooltip title="New Chat">
          <IconButton
            className={classes.iconButton}
            size="small"
            onClick={handleNewChat}>
            <AddRounded fontSize="small" />
          </IconButton>
        </Tooltip>
        <ChatHistory />
      </Box>
    </Box>
  );
}

export function ChatHistory() {
  const dispatch = useDispatch();
  const { selectedDomain } = useSelector((state) => state.domain);
  const { classes } = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pidSessions, setPidSessions] = useState([]);
  const history = useHistory();
  const location = useLocation();
  const fileId = new URLSearchParams(location.search).get("fileId");
  const tabId = new URLSearchParams(location.search).get("tabId");
  const { sessionId } = useParams();
  const pidSessionsGroupedByDate = useMemo(() => {
    return groupByDate(pidSessions);
  }, [pidSessions]);

  const fetchChatHistory = async () => {
    try {
      setIsLoading(true);
      const allSessions = await dispatch(getAllSessions(selectedDomain.id));
      const pidSessions = allSessions.filter(
        (session) => session.chat_mode === "pid"
      );
      setPidSessions(pidSessions);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchChatHistory();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSessionClick = (session) => {
    let queryParams = new URLSearchParams();
    if (fileId) {
      queryParams.set("fileId", fileId);
    }
    if (tabId) {
      queryParams.set("tabId", tabId);
    }
    history.push(`${PID_URL}/${session.id}?${queryParams.toString()}`);
    handleClose();
  };

  return (
    <Fragment>
      <Tooltip title="Show Chat History">
        <IconButton
          className={classes.iconButton}
          size="small"
          onClick={handleOpen}>
          <HistoryRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: { maxHeight: 500, overflowY: "auto" } } }}>
        {isLoading ? (
          <Loader />
        ) : (
          <Fragment>
            {pidSessions.length === 0 && (
              <Typography variant="body2" color="text.secondary" px={1}>
                No PID Sessions
              </Typography>
            )}
            {pidSessionsGroupedByDate.map((sessionGroupedByDate, i) => {
              const { date, items } = sessionGroupedByDate;

              return (
                <Fragment key={date}>
                  <ListSubheader sx={{ lineHeight: "24px" }}>
                    <Typography variant="overline">
                      <span className={classes.gradientText}>{date}</span>
                    </Typography>
                  </ListSubheader>
                  {items.map((session, j) => {
                    return (
                      <MenuItem
                        onClick={() => handleSessionClick(session)}
                        selected={session.id === sessionId}>
                        <ListItemText>{session?.title}</ListItemText>
                      </MenuItem>
                    );
                  })}
                </Fragment>
              );
            })}
          </Fragment>
        )}
      </Menu>
    </Fragment>
  );
}

export default Header;
